import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useToast } from '../components/Toast.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import { getPricing, createCheckout } from '../api/billing.js';

function formatFeatures(features) {
  if (!features || typeof features !== 'object') return [];
  const labels = [];
  if (features.max_resolution) labels.push(`Up to ${features.max_resolution} resolution`);
  if (features.credits_per_cycle) labels.push(`${features.credits_per_cycle} credits per cycle`);
  if (features.max_duration_hours) labels.push(`Up to ${features.max_duration_hours}hr video duration`);
  if (features.retention_hours) labels.push(`${features.retention_hours}hr file retention`);
  if (features.priority_rendering) labels.push('Priority rendering');
  return labels;
}

export default function PricingPage() {
  const [pricing, setPricing] = useState(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const { isAuthenticated } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  async function fetchPricing() {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getPricing();
      setPricing(data);
    } catch (err) {
      setError(err.message || 'Failed to load pricing');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchPricing();
  }, []);

  async function handleSubscribe(tierName) {
    if (!isAuthenticated) {
      navigate('/register');
      return;
    }

    setCheckoutLoading(tierName);
    try {
      const data = await createCheckout(tierName, isAnnual ? 'annual' : 'monthly');
      window.location.href = data.checkout_url;
    } catch (err) {
      showToast(err.message || 'Failed to start checkout', 'error');
      setCheckoutLoading(null);
    }
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading pricing..." />;
  }

  if (error || !pricing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
        <p className="text-red-400">{error || 'Unable to load pricing.'}</p>
        <button
          onClick={fetchPricing}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-16">
      <h1 className="text-3xl font-bold text-white mb-4 text-center">Choose Your Plan</h1>
      <p className="text-gray-400 text-center mb-10">
        Start creating professional ambient videos today.
      </p>

      {/* Monthly / Annual toggle */}
      <div className="flex items-center justify-center gap-3 mb-12">
        <span className={`text-sm ${!isAnnual ? 'text-white font-medium' : 'text-gray-400'}`}>
          Monthly
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={isAnnual}
          onClick={() => setIsAnnual(!isAnnual)}
          className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
            isAnnual ? 'bg-indigo-600' : 'bg-gray-700'
          }`}
        >
          <span
            className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
              isAnnual ? 'translate-x-7' : 'translate-x-1'
            }`}
          />
        </button>
        <span className={`text-sm ${isAnnual ? 'text-white font-medium' : 'text-gray-400'}`}>
          Annual
        </span>
      </div>

      {/* Tier cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {pricing.tiers.map((tier) => {
          const isPro = tier.name.toLowerCase() === 'pro';
          const monthlyPrice = tier.monthly_price_cents / 100;
          const annualMonthlyPrice = Math.round(tier.annual_price_cents / 100 / 12);

          return (
            <div
              key={tier.name}
              className={`bg-gray-900 rounded-xl border p-6 sm:p-8 flex flex-col ${
                isPro ? 'border-indigo-500' : 'border-gray-800'
              }`}
            >
              {isPro && (
                <span className="inline-block self-start bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
                  Most Popular
                </span>
              )}

              <h2 className="text-xl font-bold text-white mb-2">{tier.name}</h2>

              <div className="mb-6">
                {isAnnual ? (
                  <>
                    <span className="text-4xl font-bold text-white">${annualMonthlyPrice}</span>
                    <span className="text-gray-400 text-sm">/mo (billed annually)</span>
                  </>
                ) : (
                  <>
                    <span className="text-4xl font-bold text-white">${monthlyPrice}</span>
                    <span className="text-gray-400 text-sm">/mo</span>
                  </>
                )}
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {formatFeatures(tier.features).map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-gray-300 text-sm">
                    <svg
                      className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => handleSubscribe(tier.name.toLowerCase())}
                disabled={checkoutLoading === tier.name.toLowerCase()}
                className="bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-lg w-full font-medium disabled:opacity-50 transition-colors"
              >
                {checkoutLoading === tier.name.toLowerCase()
                  ? 'Redirecting...'
                  : isAuthenticated
                    ? 'Subscribe'
                    : 'Sign up to subscribe'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
